import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Button, Col, Dropdown, Grid, Layout, Row, Typography } from "antd";
import type { MenuProps } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import { ThemeContext } from "../../contexts/ThemeContext";

const { Header } = Layout;
const { Title } = Typography;
const { useBreakpoint } = Grid;

export type FullScreenNavigationHeaderLink = {
  key: string;
  label: ReactNode;
  href?: string;
  onClick?: () => void;
  active?: boolean;
};

export type FullScreenNavigationHeaderProps = {
  navLinks?: FullScreenNavigationHeaderLink[];
  rightLinks?: FullScreenNavigationHeaderLink[];
  mobileMenuItems?: MenuProps["items"];
  mobileTitle?: ReactNode;
  mobileMenuButtonAriaLabel?: string;
  showMobileMenu?: boolean;
  logoSlot?: React.ReactNode;
  sticky?: boolean;
  className?: string;
  style?: CSSProperties;
  maxWidth?: number;
};

const renderLink = (link: FullScreenNavigationHeaderLink) => (
  <Button
    key={link.key}
    type="text"
    href={link.onClick ? undefined : link.href}
    onClick={link.onClick}
    style={{
      fontWeight: link.active ? 600 : undefined,
    }}
  >
    {link.label}
  </Button>
);

const fallbackMenuItems = (
  links: FullScreenNavigationHeaderLink[]
): MenuProps["items"] =>
  links.map((link) => ({
    key: link.key,
    label: link.label,
    onClick: () => {
      if (link.onClick) {
        link.onClick();
        return;
      }
      if (link.href) {
        window.location.href = link.href;
      }
    },
  }));

type FullScreenNavigationHeaderMobileProps = {
  items: MenuProps["items"];
  title?: React.ReactNode;
  ariaLabel: string;
};

const FullScreenNavigationHeaderMobile = ({
  items,
  title,
  ariaLabel,
}: FullScreenNavigationHeaderMobileProps) => {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const navigationTrigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!navigationOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setNavigationOpen(false);
        navigationTrigger.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [navigationOpen]);

  return (
    <Row align="middle" justify="space-between" style={{ width: "100%" }}>
      <Col flex="none">
        <Dropdown
          menu={{ items, onClick: () => setNavigationOpen(false) }}
          open={navigationOpen}
          onOpenChange={setNavigationOpen}
          classNames={{ root: "full_screen__navigation_menu" }}
          placement="bottomLeft"
          trigger={["click"]}
        >
          <Button
            type="text"
            ref={navigationTrigger}
            aria-label={ariaLabel}
            aria-haspopup="menu"
            aria-expanded={navigationOpen}
            style={{ minWidth: 44, minHeight: 44 }}
            className="full_screen__header__mobile__icon"
            icon={<MenuOutlined rev={undefined} />}
          />
        </Dropdown>
      </Col>
      <Col flex="auto" style={{ textAlign: "center" }}>
        {title ? (
          <Title
            level={5}
            className="full_screen__header__mobile__title"
            style={{
              margin: 0,
            }}
          >
            {title}
          </Title>
        ) : (
          <div style={{ height: 32 }} />
        )}
      </Col>
      <Col flex="none" style={{ width: 48 }} className="full_screen__header__mobile__spacer" />
    </Row>
  );
};

export const FullScreenNavigationHeader = ({
  navLinks = [],
  rightLinks = [],
  mobileMenuItems,
  mobileTitle,
  mobileMenuButtonAriaLabel = "Open navigation",
  showMobileMenu = true,
  logoSlot,
  sticky = true,
  className,
  style,
  maxWidth = 960,
}: FullScreenNavigationHeaderProps) => {
  const themeContext = useContext(ThemeContext);
  const screens = useBreakpoint();
  const isDesktop = screens?.md ?? false;

  const effectiveTheme = themeContext?.effectiveTheme ?? "light";
  const isDark = effectiveTheme === "dark";

  const items = useMemo(() => {
    if (mobileMenuItems && mobileMenuItems.length > 0) {
      return mobileMenuItems;
    }
    return fallbackMenuItems([...navLinks, ...rightLinks]);
  }, [mobileMenuItems, navLinks, rightLinks]);

  const wrapperStyle: CSSProperties = {
    position: sticky ? "sticky" : "static",
    top: 0,
    width: "100%",
    zIndex: 20,
    ...style,
  };

  const backgroundStyle: CSSProperties = {
    backgroundColor: isDark ? "#141414" : "#f5f5f5",
    minHeight: 48,
    borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#eeeeee"}`,
  };

  const headerStyle: CSSProperties = {
    backdropFilter: "blur(6px)",
    backgroundColor: isDark ? "rgba(16,16,16,0.85)" : "rgba(255,255,255,0.85)",
    borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
    padding: isDesktop ? "0 48px" : "0 16px",
    display: "flex",
    alignItems: "center",
    minHeight: 72,
    width: "100%",
  };

  return (
    <div className={["full_screen__header-wrapper", className].filter(Boolean).join(" ")} style={wrapperStyle}>
      <Header className="full_screen__header__background" style={backgroundStyle} />
      <Header className="full_screen__header" style={headerStyle}>
        {isDesktop ? (
          <Row
            justify="space-between"
            align="middle"
            style={{ width: "100%", maxWidth, margin: "0 auto", gap: 16 }}
            className="full_screen__header__nav_bar"
          >
            {logoSlot ? (
              <Col flex="none" style={{ display: "flex", alignItems: "center" }}>
                {logoSlot}
              </Col>
            ) : null}
            <Col flex="auto" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {navLinks.map(renderLink)}
            </Col>
            <Col flex="none" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {rightLinks.map(renderLink)}
            </Col>
          </Row>
        ) : (
          showMobileMenu && (
            <FullScreenNavigationHeaderMobile
              items={items}
              title={mobileTitle}
              ariaLabel={mobileMenuButtonAriaLabel}
            />
          )
        )}
      </Header>
    </div>
  );
};

export default FullScreenNavigationHeader;
